import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Loading from "../common/Loading";
import { ACCENT } from "../artistStudio/shared";
import { BillingToggle, PlanCard } from "./FanMembershipPlans";
import { fanMembershipApi, apiErrorMessage, formatDate } from "./fanMembershipApi";
import { useErrorToast } from "../common/errorToast";

// The plans shown on an artist's page when the fan presses "See plans", so they do not leave the page. Choosing a plan goes
// to checkout with ?artist=<slug>, so the membership is credited to this artist. Prices, periods and notes come from the server.
export default function ArtistPlansPanel({ slug }) {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState("");
  useErrorToast(data ? error : ""); // a failed switch of period is a popup; with no plans loaded yet the retry panel shows it
  const latest = useRef(0);

  const load = useCallback(async (nextPeriod, initial = false) => {
    const ticket = ++latest.current;
    if (initial) setLoading(true); else setSwitching(true);
    setError("");
    try {
      const result = await fanMembershipApi.plans(nextPeriod);
      if (ticket !== latest.current) return;
      setData(result);
      setPeriod(nextPeriod);
    } catch (e) {
      if (ticket !== latest.current) return;
      setError(apiErrorMessage(e, "The plans could not be loaded. Please try again."));
    } finally {
      if (ticket === latest.current) { setLoading(false); setSwitching(false); }
    }
  }, []);

  useEffect(() => { load("monthly", true); }, [load]);

  const choose = (plan) => {
    const q = new URLSearchParams({ plan: plan.code, period, price: String(plan.price), artist: slug });
    navigate(`/fan-membership/checkout?${q.toString()}`);
  };

  if (loading) return <div className="flex justify-center py-10" role="status" aria-label="Loading plans"><Loading /></div>;
  if (!data) {
    return (
      <div className="py-6 text-center">
        <p className="text-gray-700 mb-3" role="alert">{error || "The plans could not be loaded."}</p>
        <button type="button" onClick={() => load(period, true)} className="rounded-full font-semibold text-white px-6 py-2.5" style={{ backgroundColor: ACCENT }}>Try again</button>
      </div>
    );
  }
  const current = data.current_subscription;
  return (
    <div className="mt-4" aria-label="Plans">
      <div className="text-center">
        <BillingToggle options={data.billing_periods} value={period} onChange={(p) => load(p)} disabled={switching} />
        <p className="mt-3 text-sm text-gray-500" aria-live="polite">{data.period_note}</p>
      </div>
      {current && (
        <p className={`mt-4 text-center text-sm rounded-xl text-gray-800 px-4 py-3 ${current.status === "cancelled" ? "bg-amber-50" : "bg-green-50"}`} role="status">
          Your <strong>{current.plan_name}</strong> plan ({current.billing_period}) is <strong>{current.status === "cancelled" ? "cancelled" : "active"}</strong> until {formatDate(current.current_period_end)}.{" "}
          <Link to="/fan/plan" className="font-semibold underline" style={{ color: ACCENT }}>My Plan</Link>
        </p>
      )}
      <div className={`mt-8 grid gap-6 md:grid-cols-2 transition-opacity ${switching ? "opacity-60" : ""}`}>
        {data.plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} period={period} selected={false} highlighted={Boolean(plan.label)} onChoose={choose} busy={switching} />
        ))}
      </div>
    </div>
  );
}
